import { useEffect, useState } from "react";
import { Landmark, Save } from "lucide-react";
import { getUserBankDetails, saveUserBankDetails } from "../services/BankDetailsService";

const emptyDetails = {
  name: "",
  bankName: "",
  accountNumber: "",
  accountType: "",
  branchCode: "",
};

const BankingDetailsCard = ({ userId }) => {
  const [details, setDetails] = useState(emptyDetails);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!userId) return;
    getUserBankDetails(userId)
      .then((data) => {
        if (data) setDetails(data);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const handleChange = (field) => (e) =>
    setDetails((d) => ({ ...d, [field]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await saveUserBankDetails(userId, details);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-bg-surface border border-border rounded-2xl p-6">
        <p className="text-text-muted text-sm">Loading banking details…</p>
      </div>
    );
  }

  return (
    <div className="bg-bg-surface border border-border rounded-2xl p-6 card-hover">
      <div className="flex items-center gap-2 mb-6">
        <Landmark size={16} className="text-primary" />
        <h3 className="text-text-primary font-semibold text-sm">Payout account</h3>
      </div>

      <form onSubmit={handleSave} className="grid gap-4 md:grid-cols-2">
        <Field label="Account holder name" value={details.name} onChange={handleChange("name")} />
        <Field label="Bank name" value={details.bankName} onChange={handleChange("bankName")} />
        <Field label="Account number" value={details.accountNumber} onChange={handleChange("accountNumber")} />
        <Field label="Branch code" value={details.branchCode} onChange={handleChange("branchCode")} />
        <Field label="Account type" value={details.accountType} onChange={handleChange("accountType")} />

        <div className="md:col-span-2 flex items-center gap-3 mt-2">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 ingroove-gradient-bg text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:opacity-90 transition disabled:opacity-50 shadow-glow"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save details"}
          </button>
          {saved && <span className="text-success text-sm">Saved.</span>}
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange }) => (
  <label className="block">
    <span className="text-text-muted text-xs uppercase tracking-wider mb-2 block">
      {label}
    </span>
    <input
      type="text"
      value={value ?? ""}
      onChange={onChange}
      className="w-full bg-bg-base border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary transition"
    />
  </label>
);

export default BankingDetailsCard;
